/**
 * Phase 2 — System-default cascade helpers for per-table settings that today
 * only live at the practice tier but should fall back to a system default
 * when the practice has no value.
 *
 * BE CONTRACT (proposed, not yet implemented):
 * Extend `GlobalTableColumnPreference` (one record per table_name) with these
 * optional fields:
 *
 *   hidden_filters:           [str]     // filter-chip slugs hidden by default
 *                                       //   matches practice.preferences.hidden_filters[tableName]
 *   hidden_tabs:              [str]     // tab-strip slugs hidden by default
 *                                       //   matches practice.preferences.hidden_filters._table_tabs[tableName]
 *   tab_label_overrides:      {slug:s}  // slug → renamed label dictionary
 *                                       //   matches practice.preferences.tab_label_overrides[tableName]
 *   checklist_column_insertion: str    // choice field: 'after_lead_name' | 'after_treatment' | 'before_action' | 'at_end'
 *                                       //   matches per-practice TableColumnPreference.checklist_column_insertion
 *
 * All optional, default empty/null. Until the BE adds these, the helpers below
 * return whatever the practice tier has (and the dashboard behaves exactly as
 * it does today).
 *
 * RESOLUTION RULE — practice value wins. The global tier is the fallback,
 * never an override. Two structural choices:
 *
 * 1. Array-valued fields (hidden_filters, hidden_tabs): the practice's array
 *    REPLACES the global array when set. A practice can never narrow the
 *    system default by partial override; if they want fewer hides than the
 *    global, they must opt out by setting their own (possibly empty) array.
 *    This is the same semantics the dashboard already has for per-practice
 *    arrays — keeping it consistent avoids surprises.
 *
 * 2. Object-valued field (tab_label_overrides): we deep-merge with practice
 *    values winning per key. So if global renames 'attended-checklists' → 'All'
 *    and a practice renames 'attended' → 'Visit Done', the dashboard sees
 *    { 'attended-checklists': 'All', 'attended': 'Visit Done' }. This lets
 *    practices add their own renames without losing the global ones.
 */

// ── Helpers ─────────────────────────────────────────────────────────────────

const hasOwn = (obj, key) => obj != null && Object.prototype.hasOwnProperty.call(obj, key);

/**
 * @param {object} practicePrefs - `practice.preferences` object (or null)
 * @param {object} globalPref    - the GlobalTableColumnPreference record for this table (or null)
 * @param {string} tableName     - canonical parent table name (e.g. 'Newest Leads')
 * @returns {string[]}
 */
export function getEffectiveHiddenFilters(practicePrefs, globalPref, tableName) {
    const practiceVal = practicePrefs?.hidden_filters?.[tableName];
    if (Array.isArray(practiceVal)) return practiceVal;
    const globalVal = globalPref?.hidden_filters;
    return Array.isArray(globalVal) ? globalVal : [];
}

/**
 * Tab-strip slug visibility — lives on `practice.preferences.hidden_filters._table_tabs[tableName]`
 * per-practice. Global fallback uses a flat `hidden_tabs` field on the
 * per-table global record (so it's parallel to hidden_filters in structure).
 */
export function getEffectiveHiddenTabs(practicePrefs, globalPref, tableName) {
    const practiceVal = practicePrefs?.hidden_filters?._table_tabs?.[tableName];
    if (Array.isArray(practiceVal)) return practiceVal;
    const globalVal = globalPref?.hidden_tabs;
    return Array.isArray(globalVal) ? globalVal : [];
}

/**
 * Tab rename — per-slug. Practice values win on per-key basis (deep merge).
 */
export function getEffectiveTabLabel(practicePrefs, globalPref, tableName, slug, defaultLabel) {
    const practiceMap = practicePrefs?.tab_label_overrides?.[tableName];
    if (hasOwn(practiceMap, slug)) {
        const val = practiceMap[slug];
        if (typeof val === 'string' && val.trim()) return val;
    }
    const globalMap = globalPref?.tab_label_overrides;
    if (hasOwn(globalMap, slug)) {
        const val = globalMap[slug];
        if (typeof val === 'string' && val.trim()) return val;
    }
    return defaultLabel;
}

/**
 * Position for dynamic task-checklist columns. Per-practice `TableColumnPreference`
 * holds this today; global fallback comes from the same field on the global record.
 */
export function getEffectiveChecklistInsertion(practiceTablePref, globalPref) {
    if (practiceTablePref?.checklist_column_insertion) return practiceTablePref.checklist_column_insertion;
    if (globalPref?.checklist_column_insertion) return globalPref.checklist_column_insertion;
    return 'after_lead_name';
}

/**
 * Convenience: pull the global record for a single table from the
 * `globalTablePreferences` slice payload (which can be an array or paginated
 * `{results: []}`). Returns null when not found.
 */
export function selectGlobalPrefForTable(globalState, tableName) {
    if (!globalState) return null;
    const list = Array.isArray(globalState) ? globalState : (globalState.results || []);
    return list.find((r) => r?.table_name === tableName) || null;
}
